import { useId, useState, type FormEvent } from 'react';
import { stepupAuthenticator, stepupOptions, stepupVerify } from './api';
import { describeAuthError, askForPasskey } from './passkeys';

/** "Confirm it's you": shown before a sensitive change if you haven't signed in recently. */
export function StepUpPanel({
  hasAuthenticator,
  onVerified,
  onCancel,
}: {
  hasAuthenticator: boolean;
  onVerified: () => Promise<void>;
  onCancel: () => void;
}) {
  const id = useId();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(check: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await check();
      await onVerified();
    } catch (err) {
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  const withPasskey = () =>
    run(async () => {
      const { challengeId, options } = await stepupOptions();
      await stepupVerify(challengeId, await askForPasskey(options));
    });

  function withCode(e: FormEvent) {
    e.preventDefault();
    if (code.trim()) void run(() => stepupAuthenticator(code.trim()));
  }

  return (
    <section
      className="card card-warn"
      role="dialog"
      aria-labelledby={`${id}-title`}
      data-testid="stepup"
    >
      <h2 id={`${id}-title`} className="card-heading">
        Confirm it's you
      </h2>
      <p className="card-detail">
        You haven't signed in for a few minutes. Confirm once more to make this change.
      </p>
      <div className="button-row">
        <button
          type="button"
          className="primary"
          disabled={busy}
          onClick={() => void withPasskey()}
        >
          Confirm with passkey
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
      {hasAuthenticator && (
        <form onSubmit={withCode} className="stack">
          <label htmlFor={id}>Or enter an authenticator code</label>
          <input
            id={id}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <button type="submit" disabled={busy || code.trim() === ''}>
            Confirm with code
          </button>
        </form>
      )}
      {error && (
        <p role="alert" className="error-text">
          {error}
        </p>
      )}
    </section>
  );
}
